import {
  notify,
  loadingBlock,
  confirmation,
} from '../../../helpers/notifications';
import dayjs from 'dayjs';
import Papa from 'papaparse';
import { handleCreateOption, handleUpdateOption } from './handleOption';
import { createMC, updateMC } from '../../metadata/constants/handleMetaContent';
import {
  createCalendarEvent,
  updateCalendarEvent,
} from '../../metadata/constants/handleCalendarEvents';
import { downloadBlob } from '../../../helpers/downloadBlob';

const COUNTS = {
  optSuccess: 0,
  optSkip: 0,
  metaSuccess: 0,
  metaSkip: 0,
  metaErr: 0,
  icalSuccess: 0,
  icalSkip: 0,
  icalErr: 0,
};

const csvFuncs = {
  refreshPage: () => {
    loadingBlock.remove('.options');
    location.reload();
  },
  notifyResult: function (counts, total, report) {
    let self = this;
    const includeMeta = counts.metaSuccess || counts.metaSkip || counts.metaErr;
    const includeIcal = counts.icalSuccess || counts.icalSkip || counts.icalErr;
    confirmation.acknowledge(
      () => self.downloadReport(report),
      this.refreshPage,
      'Success',
      `<b>Options</b>
      <br>Saved: ${counts.optSuccess}
      <br>Skipped: ${counts.optSkip}
      <br>Errors: ${total - (counts.optSuccess + counts.optSkip)}
      ${
        includeMeta
          ? `<br><br><b>Metadata</b>
      <br>Saved: ${counts.metaSuccess}
      <br>Skipped: ${counts.metaSkip}
      <br>Errors: ${counts.metaErr}`
          : ''
      }
      ${
        includeIcal
          ? `<br><br><b>Calendar Events</b>
        <br>Saved: ${counts.icalSuccess}
        <br>Skipped: ${counts.icalSkip}
        <br>Errors: ${counts.icalErr}`
          : ''
      }
      <br><br>Download results report?`
    );
  },
  getMetaKeys: (row) => {
    return Object.keys(row).filter((k) => k.indexOf('me_') != -1);
  },
  getIcalKeys: (row) => {
    return Object.keys(row)
      .filter((k) => k.indexOf('ical_') != -1)
      .map((key) => key.split('ical_')[1]);
  },
  normalizeColumnKeys: (row) => {
    let downcasedKeys = Object.entries(row).map(([key, value]) => [
      key.toLowerCase(),
      value,
    ]);
    return Object.fromEntries(downcasedKeys);
  },
  metaError: function () {
    notify.alertFailure(
      'Metadata Error',
      'Invalid columns, at least one metadata entry does not exist.',
      this.refreshPage
    );
  },
  icalError: function (error) {
    notify.alertFailure('Calendar Event Error', error, this.refreshPage);
  },
  optionError: function (error) {
    notify.alertFailure('Option Error', error, this.refreshPage);
  },
  buildReport: function (header, optionResultsArray) {
    // params constructed during the CSV create / update process;
    let csvReport = Papa.unparse({
      fields: header,
      data: optionResultsArray,
    });
    return csvReport;
  },
  downloadReport: function (csvReport) {
    let blob = new Blob([csvReport], { type: 'text/csv' });
    downloadBlob(blob, 'options_result.csv');
    this.refreshPage();
  },
  formatErrors: function (errObj) {
    return Object.entries(errObj)
      .map(([key, value]) => `[${key}: ${value}]`)
      .join('; ');
  },
};

const icalFuncs = {
  requiredKeys: ['summary', 'description', 'dtstart', 'dtend'],
  optionalKeys: ['location', 'timezone', 'alert_trigger'],
  validKeys: function () {
    return this.requiredKeys.concat(this.optionalKeys);
  },
  buildIcalObject: function (csvRow, providedKeys) {
    let ical = {};
    providedKeys
      .filter((key) => this.validKeys().includes(key.toLowerCase()))
      .forEach((key) => {
        if (csvRow[`ical_${key}`]) ical[key] = csvRow[`ical_${key}`];
      });

    // convert times if present
    if (ical.dtstart && dayjs(ical.dtstart).isValid())
      ical.dtstart = dayjs(ical.dtstart).toISOString();
    if (ical.dtend && dayjs(ical.dtend).isValid())
      ical.dtend = dayjs(ical.dtend).toISOString();

    return ical;
  },
  icalValidation: function (ical) {
    let error = '';
    if (!ical.summary || !ical.description || !ical.dtstart || !ical.dtend) {
      let missing = icalFuncs.requiredKeys.filter((k) => !ical[k]);
      error = `Missing required column(s): ${missing.join(', ')}`;
    } else if (!dayjs(ical.dtstart).isValid() || !dayjs(ical.dtend).isValid()) {
      error = 'DateTime parse error for ical column(s)';
    } else if (
      ical.timezone &&
      !['America/Chicago', 'America/Denver'].includes(ical.timezone)
    ) {
      error = `Timezone must be America/Chicago, America/Denver, or blank (defaults to America/Chicago).`;
    }
    return { error };
  },
};

export const parseAndCreateOptions = (file, params) => {
  if (!file) return;
  const { field, options, metaEntries } = params;
  const selectOrRadio = ['select', 'radio'].includes(field.type);
  loadingBlock.add('.options', 'Creating Options...');

  Papa.parse(file, {
    header: true,
    delimiter: ',',
    dynamicTyping: true,
    skipEmptyLines: true,

    error: function (error) {
      console.error(error);
      notify.alertFailure('Error', 'Failed to parse CSV', csvFuncs.refreshPage);
    },

    complete: async function (results) {
      let tmp, option, calendarEvent;
      let fieldMEs = metaEntries || [];
      let counts = { ...COUNTS };
      let total = results.data.length;
      // define result report headers
      let reportHeader = ['Option_ID', 'Option_Name', 'Result'];
      let reportArray = [];
      // use CSV first row to prepare some useful data
      const firstRow = csvFuncs.normalizeColumnKeys(results.data[0]);
      const entries = csvFuncs.getMetaKeys(firstRow);
      const metaPresent = selectOrRadio && entries.length;
      if (metaPresent) entries.forEach((e) => reportHeader.push(e));
      const icalData = csvFuncs.getIcalKeys(firstRow);
      const icalHeadersPresent = icalData.length > 0;
      if (icalHeadersPresent) reportHeader.push('ICS_file');

      // iterate on each csv row
      for (let i = 0; i < total; i++) {
        tmp = csvFuncs.normalizeColumnKeys(results.data[i]);
        // default each report header value with 'skip', overwrite as needed
        let reportRow = {};
        reportHeader.forEach((key) => (reportRow[key] = 'skip'));
        reportRow['Option_Name'] =
          tmp['name'] || 'ERROR: unable to determine option name';

        option = {
          name: tmp['name'],
          value: tmp['value'] || tmp['name'],
          local_quota: tmp['local_quota'] || 0,
          field_id: field.id,
          sort: options.length + i,
        };

        const opt = await handleCreateOption(option);
        if (opt.ok) {
          counts.optSuccess += 1;
          let optId = opt.data.id.toString();
          reportRow['Option_ID'] = optId;
          reportRow['Result'] = 'OK: created';

          // Handle metadata if present
          if (metaPresent) {
            let promises = entries.map(async (e) => {
              let me = fieldMEs.find(
                (me) => e == 'me_' + me.title.split(' ').join('_').toLowerCase()
              );
              if (me) {
                // Create metadata contents if description text present
                let desc = tmp[e] ? tmp[e].toString() : '';
                if (desc) {
                  const mc = await createMC(me.id.toString(), desc, optId);
                  mc.ok ? (counts.metaSuccess += 1) : (counts.metaErr += 1);
                  reportRow[e] = mc.ok
                    ? 'OK: created'
                    : csvFuncs.formatErrors(mc.errors);
                } else {
                  counts.metaSkip += 1;
                }
              } else {
                counts.metaErr += 1;
                reportRow[e] = `ERROR: Metadata Entry not found`;
              }
            });
            await Promise.all(promises);
          }

          // Create calendar event if data present and valid
          if (selectOrRadio && icalHeadersPresent) {
            calendarEvent = icalFuncs.buildIcalObject(tmp, icalData);
            let validation = icalFuncs.icalValidation(calendarEvent);
            if (!Object.keys(calendarEvent).length) {
              counts.icalSkip += 1;
            } else if (validation.error.length > 0) {
              counts.icalErr += 1;
              reportRow['ICS_file'] = `FAILED: ${validation.error}`;
            } else {
              const ce = await createCalendarEvent({
                ...calendarEvent,
                option_id: optId,
              });
              ce.ok ? (counts.icalSuccess += 1) : (counts.icalErr += 1);
              reportRow['ICS_file'] = ce.ok
                ? 'OK: created'
                : `FAILED: ${csvFuncs.formatErrors(ce.errors)}`;
            }
          }
          // Push collected results to reportArray
          reportArray.push(Object.values(reportRow));
        } else {
          // abort other requests, log error into CSV report, return to loop;
          reportRow['Option_ID'] = 'ERROR';
          reportRow['Result'] = `FAILED: ${csvFuncs.formatErrors(opt.errors)}`;
          reportArray.push(Object.values(reportRow));
          if (i == total - 1) {
            let report = csvFuncs.buildReport(reportHeader, reportArray);
            return csvFuncs.notifyResult(counts, total, report);
          } else {
            continue;
          }
        }
        // Notify results after last option row finishes
        if (i == total - 1) {
          let report = csvFuncs.buildReport(reportHeader, reportArray);
          return csvFuncs.notifyResult(counts, total, report);
        }
      }
    },
  });
};

/*
 * Only update columns that have data input. ID column required.
 * Build a CSV report of all update results for user to review.
 */
export const parseAndUpdateOptions = (file, params) => {
  if (!file) return;
  const { field, options, metaEntries } = params;
  const selectOrRadio = ['select', 'radio'].includes(field.type);
  loadingBlock.add('.options', 'Updating Options...');

  Papa.parse(file, {
    header: true,
    delimiter: ',',
    dynamicTyping: true,
    skipEmptyLines: true,

    error: function (error) {
      console.error(error);
      notify.alertFailure('Error', 'Failed to parse CSV', csvFuncs.refreshPage);
    },

    complete: async function (results) {
      let tmp, option;
      let fieldMEs = metaEntries || [];
      let counts = { ...COUNTS };
      let total = results.data.length;
      // define result report headers
      let reportHeader = ['Option_ID', 'Option_Name', 'Result'];
      let reportArray = [];
      // use CSV first row to prepare some useful data
      const firstRow = csvFuncs.normalizeColumnKeys(results.data[0]);
      const entries = csvFuncs.getMetaKeys(firstRow);
      const metaPresent = selectOrRadio && entries.length;
      if (metaPresent) entries.forEach((e) => reportHeader.push(e));
      const icalData = csvFuncs.getIcalKeys(firstRow);
      const icalHeadersPresent = icalData.length > 0;
      if (icalHeadersPresent) reportHeader.push('ICS_file');

      // iterate on each csv row
      for (let i = 0; i < total; i++) {
        tmp = csvFuncs.normalizeColumnKeys(results.data[i]);
        let optionToUpdate = options.find((o) => o.id.toString() == tmp['id']);
        let reportRow = {};
        reportHeader.forEach((key) => (reportRow[key] = 'skip'));
        reportRow['Option_ID'] = tmp['id'] || 'skip';
        reportRow['Option_Name'] =
          tmp['name'] ||
          optionToUpdate?.name ||
          'ERROR: unable to determine option name';

        // Skip row if missing id value, or if option id not found in field's options
        // If this is the last row, notify results, else continue
        if (!tmp['id'] || !optionToUpdate) {
          let reason = !tmp['id']
            ? 'option id not provided'
            : `option ${tmp['id']} does not belong to field`;
          counts.optSkip += 1;
          if (icalHeadersPresent) counts.icalSkip += 1;
          if (metaPresent) counts.metaSkip += entries.length;
          reportRow['Result'] = `SKIP: ${reason}`;
          reportArray.push(Object.values(reportRow));
          if (i == total - 1) {
            let report = csvFuncs.buildReport(reportHeader, reportArray);
            return csvFuncs.notifyResult(counts, total, report);
          } else {
            continue;
          }
        }

        let eventToUpdate = optionToUpdate.calendar_event;
        let metaContents = optionToUpdate.metadata_contents;
        option = {
          name: tmp['name'] || optionToUpdate.name,
          value: tmp['value'] || optionToUpdate.value,
          local_quota: tmp['local_quota'] || optionToUpdate.local_quota,
        };

        // Only attempt option model update if values present
        if (tmp['name'] || tmp['value'] || tmp['local_quota']) {
          const opt = await handleUpdateOption(optionToUpdate.id, option);
          if (opt.ok) {
            counts.optSuccess += 1;
            reportRow['Result'] = 'OK: updated';
          } else {
            reportRow['Result'] =
              `FAILED: ${csvFuncs.formatErrors(opt.errors)}`;
          }
        } else {
          counts.optSkip += 1;
          reportRow['Result'] = 'SKIP: no changes found';
        }

        // Handle metadata if present
        if (metaPresent) {
          let promises = entries.map(async (e) => {
            let me = fieldMEs.find(
              (me) => e == 'me_' + me.title.split(' ').join('_').toLowerCase()
            );
            if (me) {
              let contentToUpdate = metaContents.find(
                (c) => c.metadata_entry_id == me.id
              );
              let desc = tmp[e] ? tmp[e].toString() : '';
              // update content if it exists, else create it if text present
              if (desc.length > 0) {
                const mc = contentToUpdate
                  ? await updateMC(contentToUpdate.id, desc)
                  : await createMC(
                      me.id.toString(),
                      desc,
                      optionToUpdate.id.toString()
                    );

                mc.ok ? (counts.metaSuccess += 1) : (counts.metaErr += 1);
                reportRow[e] = mc.ok
                  ? `OK: (${contentToUpdate ? 'updated' : 'created'})`
                  : csvFuncs.formatErrors(mc.errors);
              } else {
                counts.metaSkip += 1;
              }
            } else {
              counts.metaErr += 1;
              reportRow[e] = `ERROR: Metadata Entry not found`;
            }
          });
          await Promise.all(promises);
        }

        // Handle calendar event if present
        if (selectOrRadio && icalHeadersPresent) {
          let calendarEvent = icalFuncs.buildIcalObject(tmp, icalData);
          let calDataPresent = Object.keys(calendarEvent).length > 0;
          if (eventToUpdate && calDataPresent)
            calendarEvent = { ...eventToUpdate, ...calendarEvent };
          let validation = icalFuncs.icalValidation(calendarEvent);
          if (!calDataPresent) {
            counts.icalSkip += 1;
          } else if (validation.error.length > 0) {
            counts.icalErr += 1;
            reportRow['ICS_file'] = `FAILED: ${validation.error}`;
          } else {
            const ce = eventToUpdate
              ? await updateCalendarEvent(eventToUpdate.id, calendarEvent)
              : await createCalendarEvent({
                  ...calendarEvent,
                  option_id: optionToUpdate.id,
                });
            ce.ok ? (counts.icalSuccess += 1) : (counts.icalErr += 1);
            reportRow['ICS_file'] = ce.ok
              ? `OK: (${eventToUpdate ? 'updated' : 'created'})`
              : `FAILED: ${csvFuncs.formatErrors(ce.errors)}`;
          }
        }

        // Push collected results to reportArray
        reportArray.push(Object.values(reportRow));
        // Notify results after last option row finishes
        if (i == total - 1) {
          let report = csvFuncs.buildReport(reportHeader, reportArray);
          return csvFuncs.notifyResult(counts, total, report);
        }
      }
    },
  });
};
